import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Download, X, AlertTriangle, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Check twice: at launch and every 30 min in case the app stays open for hours.
const RECHECK_INTERVAL_MS = 30 * 60 * 1000;
// Ignore soft-update reminders for 24h once user dismisses them.
const SOFT_DISMISS_STORAGE_KEY = 'rv_soft_update_dismissed_until';

/**
 * ForceUpdateGuard — runs only on native Capacitor apps (android / ios).
 * Silently skipped on PWA / browser because the service worker handles
 * updates automatically.
 *
 * States:
 *   - hard_update → full-screen non-dismissible overlay with a CTA that opens
 *     the respective store.
 *   - soft_update → dismissible toast at the top of the screen; dismissal
 *     persists for 24h so the user isn't nagged.
 *   - up_to_date / web → nothing is rendered.
 */
const ForceUpdateGuard = () => {
  const [state, setState] = useState(null); // { status, store_url, latest_version }
  const checkedOnceRef = useRef(false);

  const openStore = async (url) => {
    if (!url) return;
    // On native (Android/iOS) we must use the Capacitor Browser plugin or
    // an `intent://` / `itms-apps://` scheme to hand off to the store app.
    // `window.open` inside the Capacitor WebView is unreliable and silently
    // fails on iOS — this was the "popup non apre lo store" bug.
    try {
      const platform = window.Capacitor?.getPlatform?.() || 'web';
      // Try a deep-link to the native store first (instant store-app launch)
      let deepLink = url;
      if (platform === 'android' && url.includes('play.google.com')) {
        // market://details?id=... opens Play Store app directly
        const idMatch = url.match(/[?&]id=([^&]+)/);
        if (idMatch) deepLink = `market://details?id=${idMatch[1]}`;
      } else if (platform === 'ios' && url.includes('apps.apple.com')) {
        // itms-apps:// opens App Store app directly
        deepLink = url.replace(/^https?:\/\/apps\.apple\.com/, 'itms-apps://apps.apple.com');
      }

      if (platform === 'android' || platform === 'ios') {
        // Use Capacitor Browser plugin — it routes through the OS and triggers
        // the store-app deep-link handler. Falls back to in-app browser.
        try {
          const { Browser } = await import('@capacitor/browser');
          await Browser.open({ url: deepLink, presentationStyle: 'fullscreen' });
          return;
        } catch (e) {
          // Plugin not available → fall back to window.location which on
          // Capacitor 6+ also triggers the OS URL handler.
          window.location.href = deepLink;
          return;
        }
      }
      // Web/PWA: standard new-tab
      window.open(url, '_blank');
    } catch (e) {
      // Last-resort fallback
      try { window.location.href = url; } catch { /* ignore */ }
    }
  };

  useEffect(() => {
    let mounted = true;
    let intervalId = null;

    const isNative = () => {
      try {
        return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
      } catch {
        return false;
      }
    };

    const detectPlatform = () => {
      try {
        const p = window.Capacitor?.getPlatform?.();
        if (p === 'ios' || p === 'android') return p;
      } catch { /* ignore */ }
      return 'web';
    };

    const getAppVersionInfo = async () => {
      // iter102: also grab `build` (Android versionCode / iOS
      // CFBundleVersion). We compare on `build` server-side because
      // internal releases often bump ONLY the versionCode, keeping the
      // versionName the same (that's exactly why the 2026-09 release
      // didn't trigger the "aggiorna" popup for anyone).
      try {
        const { App } = await import('@capacitor/app');
        const info = await App.getInfo();
        return {
          version: info?.version || null,
          build: info?.build ? String(info.build) : null,
        };
      } catch {
        return { version: null, build: null };
      }
    };

    const check = async () => {
      if (!isNative()) return;
      const platform = detectPlatform();
      if (platform === 'web') return;

      const { version, build } = await getAppVersionInfo();
      if (!version && !build) return;

      try {
        const { data } = await axios.get(`${API}/app/version-check`, {
          params: { version: version || '0.0.0', platform, build: build || undefined },
          timeout: 8000,
        });
        if (!mounted) return;

        if (data.status === 'soft_update') {
          // Skip if user already dismissed today
          const dismissedUntil = Number(localStorage.getItem(SOFT_DISMISS_STORAGE_KEY) || 0);
          if (Date.now() < dismissedUntil) return;
          if (!checkedOnceRef.current) {
            checkedOnceRef.current = true;
            // iter102: was a Sonner toast at the top of the screen, but users
            // reported they never noticed it. We now render a proper centered
            // modal (setState → SoftUpdateModal below) — dismissible with X
            // but far more visible. Snooze still applies on close.
            setState(data);
          }
          return;
        }

        if (data.status === 'hard_update') {
          setState(data);
          return;
        }

        // up_to_date / unknown_platform → clear any stale overlay
        setState(null);
      } catch (_) {
        // Network/backend down: do NOT block the app — fail silently.
      }
    };

    check();
    intervalId = setInterval(check, RECHECK_INTERVAL_MS);
    return () => {
      mounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  if (!state || (state.status !== 'hard_update' && state.status !== 'soft_update')) return null;

  const isHard = state.status === 'hard_update';

  const handleDismiss = () => {
    if (isHard) return; // hard update non è dismissibile
    localStorage.setItem(SOFT_DISMISS_STORAGE_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
    setState(null);
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-[#0a0a0a]/98 backdrop-blur-md flex items-center justify-center p-6"
      data-testid={isHard ? 'force-update-overlay' : 'soft-update-overlay'}
      onClick={isHard ? undefined : handleDismiss}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-w-sm w-full bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] rounded-3xl border border-[#F5A623]/30 p-7 shadow-2xl shadow-[#F5A623]/10 relative"
      >
        {!isHard && (
          <button
            onClick={handleDismiss}
            aria-label="Chiudi"
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-400"
            data-testid="soft-update-close"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        <div className="flex flex-col items-center text-center">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#F5A623] to-[#E08900] flex items-center justify-center mb-5 shadow-[0_0_30px_rgba(245,166,35,0.4)]">
            {isHard ? (
              <AlertTriangle className="w-10 h-10 text-black" />
            ) : (
              <Sparkles className="w-10 h-10 text-black" />
            )}
          </div>

          <h2 className="text-2xl font-black text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            {isHard ? 'AGGIORNAMENTO RICHIESTO' : 'NUOVO AGGIORNAMENTO DISPONIBILE'}
          </h2>

          <p className="text-gray-400 text-sm leading-relaxed mb-6">
            {isHard ? (
              <>La versione installata dell'app non è più supportata.<br />Aggiorna gratuitamente all'ultima versione per continuare a usare RVincente.</>
            ) : (
              <>È disponibile una versione aggiornata con nuove funzioni e correzioni.<br />Aggiorna ora per non perderti nulla.</>
            )}
          </p>

          <div className="w-full bg-black/40 rounded-xl p-3 mb-6 flex items-center justify-between text-xs">
            <div>
              <div className="text-gray-500">Installata</div>
              <div className="text-white font-mono font-bold">
                v{state.installed_version}
                {state.installed_build ? <span className="text-gray-500 font-normal"> ({state.installed_build})</span> : null}
              </div>
            </div>
            <div className="text-[#F5A623]">→</div>
            <div>
              <div className="text-gray-500">Disponibile</div>
              <div className="text-[#F5A623] font-mono font-bold">
                v{state.latest_version}
                {state.latest_build ? <span className="text-[#F5A623]/70 font-normal"> ({state.latest_build})</span> : null}
              </div>
            </div>
          </div>

          <button
            onClick={() => openStore(state.store_url)}
            className="w-full py-4 rounded-2xl bg-gradient-to-r from-[#F5A623] to-[#E08900] text-black font-black text-base uppercase tracking-wide flex items-center justify-center gap-2 shadow-lg active:scale-95 transition-transform"
            data-testid={isHard ? 'force-update-cta' : 'soft-update-cta'}
          >
            <Download className="w-5 h-5" />
            {isHard ? 'Aggiorna ora' : "Aggiorna l'app"}
          </button>

          {!isHard && (
            <button
              onClick={handleDismiss}
              className="mt-3 text-xs font-semibold text-gray-500 hover:text-white transition-colors"
              data-testid="soft-update-later"
            >
              Più tardi
            </button>
          )}

          <p className="text-[11px] text-gray-600 mt-4">
            {state.platform === 'ios' ? 'App Store' : 'Google Play Store'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForceUpdateGuard;
